import gql from 'graphql-tag'
import client from '@stores/apollo'
import { formatDate2 } from '@utils'
import { DateUtil, TIME } from '@utils/dateUtil'
import { RangePattern, RecurrenceT, TypePattern } from '@providers'
import { parseDate } from '@utils'

type CreateBookingInputT = {
  spaceId: string
  employeeId: string
  startDate: DateUtil
  endDate: DateUtil
  note?: string
}

type CreateBookingScheduleInputT = {
  recurrence: RecurrenceT
  allDaySetting: boolean
  bookingWorkdayTimeFrom: string
  ignoreConflicts?: boolean
  spaceId: string
  employeeId: string
  startDate: DateUtil
  endDate: DateUtil
  note?: string
}

type CreateSpaceBookingInputT = {
  recurrence: RecurrenceT
  allDaySetting: boolean
  bookingWorkdayTimeFrom: string
  ignoreConflicts?: boolean
  spaceId: string
  employeeId: string
  startDate: DateUtil
  endDate: DateUtil
  note?: string
  timezone: string
}

export default {
  createBooking: ({ spaceId, employeeId, startDate, endDate, note }: CreateBookingInputT) => {
    const dateValues = {
      startDate: formatDate2(startDate),
      endDate: formatDate2(endDate),
    }
    return client.gql.mutate({
      mutation: createBookingMutation,
      variables: {
        input: {
          spaceId,
          employeeId,
          note,
          ...dateValues,
        },
      },
    })
  },

  createBookingSchedule: ({
    recurrence,
    employeeId,
    spaceId,
    startDate,
    endDate,
    note,
    allDaySetting,
    bookingWorkdayTimeFrom,
    ignoreConflicts = false,
  }: CreateBookingScheduleInputT) => {
    // const bookingWorkdayTimeFromFlr = get(floor, 'bookingWorkdayTimeFrom')
    // const defaultTimeFrom = convertDateTimeToTimezone({
    //   dateTime: moment(bookingWorkdayTimeFromFlr || bookingWorkdayTimeFrom, 'HH:mm'),
    //   timezone,
    // })
    const [hour, minute] = bookingWorkdayTimeFrom.split(':').map((e) => Number(e))
    const input = {
      employeeId,
      spaceId,
      effectiveStartDate: formatDate2(startDate),
      startDate: formatDate2(
        allDaySetting
          ? startDate.clone().set({
              hour,
              minute,
            })
          : startDate
      ),
      endDate: formatDate2(endDate),
      note,
      interval: recurrence.typeInterval,
      recurrencePattern:
        recurrence.typePattern !== TypePattern.MONTHLY
          ? recurrence.typePattern.toString()
          : recurrence.monthlyType,
      recurrenceRange: recurrence.rangePattern.toString(),
      numberOfOccurrences:
        recurrence.rangePattern === RangePattern.AFTER ? recurrence.rangeInterval : undefined,
      effectiveEndDate:
        recurrence.rangePattern === RangePattern.ON_DATE
          ? formatDate2(
              recurrence.rangeDate.clone().set({
                year: recurrence.rangeDate.year(),
                month: recurrence.rangeDate.month(),
                date: recurrence.rangeDate.date(),
                hour: 23,
                minute: 59,
                second: 59,
              })
            )
          : undefined,
      daysOfWeek: recurrence.typePattern === TypePattern.WEEKLY ? recurrence.daysOfWeek : undefined,
      ignoreConflicts,
    }
    return client.gql.mutate({
      mutation: createScheduleMutation,
      variables: {
        input,
      },
    })
  },

  createSpaceBooking: ({
    employeeId,
    spaceId,
    startDate,
    endDate,
    note,
    ignoreConflicts = false,
    timezone,
  }: CreateSpaceBookingInputT) => {
    let input
    const DATE_PARSE_FORMAT = 'yyyy-MM-dd HH:mm:ss'
    const startMoment = parseDate(startDate.toDate(), timezone)
    const endMoment = parseDate(endDate.toDate(), timezone)
    const isMultiDayBooking = !startMoment.isSame(endMoment, 'days')
    const formatedStartDate = formatDate2(startDate, DATE_PARSE_FORMAT)
    const formatedEndDate = formatDate2(
      isMultiDayBooking ? endDate.clone().add(1, TIME.DAYS) : endDate,
      DATE_PARSE_FORMAT
    )
    // Multiday booking is now booked as recurring booking
    if (isMultiDayBooking) {
      input = {
        spaceId,
        startDate: formatedStartDate,
        endDate: formatedEndDate,
        note,
        effectiveStartDate: formatedStartDate,
        effectiveEndDate: formatedEndDate,
        interval: 1,
        recurrencePattern: 'multi_day',
        recurrenceRange: 'end_date',
        ignoreConflicts: ignoreConflicts,
        employeeId,
      }
    } else {
      input = {
        employeeId,
        spaceId,
        startDate: formatedStartDate,
        endDate: formatedEndDate,
        note,
      }
    }

    const mutationName = isMultiDayBooking ? createScheduleMutation : createBookingMutation

    return client.gql.mutate({
      mutation: mutationName,
      variables: {
        input,
      },
    })
  },

  // createBookingSchedule({
  //   spaceId,
  //   employeeId,
  //   startDate,
  //   endDate,
  //   effectiveEndDate,
  //   effectiveStartDate,
  //   interval,
  //   recurrencePattern,
  //   recurrenceRange,
  //   numberOfOccurrences,
  //   daysOfWeek,
  //   ignoreConflicts,
  //   sourceSingleDayBookingId,
  // }: CreateBookingScheduleInputT) {
  //   return gql
  //     .mutate(createBookingScheduleMutation, {
  //       input: {
  //         spaceId,
  //         employeeId,
  //         startDate,
  //         endDate,
  //         effectiveEndDate,
  //         effectiveStartDate,
  //         interval,
  //         recurrencePattern,
  //         recurrenceRange,
  //         numberOfOccurrences,
  //         daysOfWeek,
  //         ignoreConflicts,
  //         sourceSingleDayBookingId,
  //       },
  //     })
  //     .then((result) => result.createBookingSchedule.bookingSchedule)
  // },
}

const createScheduleMutation = gql`
  mutation createBookingsSchduleMutation($input: CreateBookingScheduleInput!) {
    createBookingSchedule(input: $input) {
      bookingSchedule {
        id
        effectiveStartDate
        effectiveEndDate
        startDate
        space {
          id
          code
          name
          externalId
          floor {
            id
            name
            building {
              timezone
              id
              name
              city
              timezone
              address
            }
          }
          usageType {
            displayName
          }
        }
        bookings {
          id
          date
          startDate
          endDate
          checkedIn
          note
          bookingSchedule {
            id
            recurrencePattern
            effectiveEndDate
            effectiveStartDate
            pattern {
              dayOfMonth
              daysOfWeek
              interval
              type
            }
            range {
              endDate
              numberOfOccurrences
              startDate
              type
            }
          }
          healthCheck {
            locationHeathCheckGroup {
              id
            }
            disabled
          }
          employee {
            id
            name
            email
            dept {
              color
              name
            }
          }
          space {
            id
            code
            name
            externalId
            floor {
              id
              name
              code
              building {
                code
                timezone
                id
                name
                city
                timezone
                address
              }
            }
            usageType {
              displayName
            }
          }
        }
        conflicts {
          startDate
          endDate
          employee {
            name
          }
          withinCapacityLimit
        }
      }
    }
  }
`

const createBookingMutation = gql`
  mutation createBookingMutation($input: CreateBookingInput!) {
    createBooking(input: $input) {
      booking {
        id
        date
        startDate
        endDate
        checkedIn
        note
        bookingSchedule {
          id
        }
        employee {
          id
          name
          email
          dept {
            color
            name
          }
        }
        healthCheck {
          disabled
          locationHeathCheckGroup {
            id
          }
        }
        space {
          id
          externalId
          floor {
            id
            name
            code
            enforceQrCode
            building {
              code
              timezone
              id
              name
              city
              timezone
              address
            }
            printableQrCodeUsageTypes
          }
          usageType {
            name
            displayName
          }
        }
      }
    }
  }
`
